import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, BookOpen, CalendarClock, CalendarDays, ClipboardCheck, ClipboardList, FolderOpen, Lock, Send, Users, type LucideIcon } from "lucide-react";

import { AssessmentsPanel, CasesPanel, LearnersPanel, PlansPanel, ReferralsPanel, ResourcesPanel, SessionsPanel } from "../components/counseling/CounselingPanels";
import { usePermissions } from "../hooks/usePermissions";
import { counselingService } from "../services/counselingService";
import { useAuthStore } from "../stores/authStore";

type Tab = "sessions" | "cases" | "learners" | "plans" | "assessments" | "referrals" | "resources";

/**
 * Counselling: the counsellor's day of sessions, the cases they hold, support
 * plans, assessments and referrals, all confidential to them (the
 * administrator sees every counsellor's). Everyone else sees only the
 * resources shared with them.
 */
export default function Counseling() {
  const { hasCapability } = usePermissions();
  const canCounsel = hasCapability("access.counseling.counseling_role");
  const isAdmin = useAuthStore((state) => state.user?.user_type) === "admin";
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") ?? undefined;
  const learner = params.get("learner") ?? undefined;

  const on = { enabled: canCounsel };
  const stats = useQuery({ queryKey: ["counseling", "stats"], queryFn: counselingService.getStats, ...on });
  const sessions = useQuery({ queryKey: ["counseling", "sessions"], queryFn: counselingService.getSessions, ...on });
  const issues = useQuery({ queryKey: ["counseling", "issues"], queryFn: counselingService.getIssues, ...on });
  const plans = useQuery({ queryKey: ["counseling", "plans"], queryFn: counselingService.getPlans, ...on });
  const assessments = useQuery({ queryKey: ["counseling", "assessments"], queryFn: counselingService.getAssessments, ...on });
  const referrals = useQuery({ queryKey: ["counseling", "referrals"], queryFn: counselingService.getReferrals, ...on });
  const resources = useQuery({ queryKey: ["counseling", "resources"], queryFn: counselingService.getResources });
  const learners = useQuery({ queryKey: ["counseling", "learners"], queryFn: counselingService.getStudents, ...on });
  const counselors = useQuery({ queryKey: ["counseling", "counselors"], queryFn: counselingService.getCounselors, enabled: canCounsel && isAdmin });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["counseling"] });
  const setup = { learners: learners.data ?? [], counselors: counselors.data ?? [], isAdmin };

  const s = stats.data;
  const tabs: { id: Tab; label: string; icon: LucideIcon; badge?: number }[] = canCounsel
    ? [
        { id: "sessions", label: "Sessions", icon: CalendarDays, badge: (s?.sessions_today ?? 0) + (s?.overdue_sessions ?? 0) },
        { id: "cases", label: "Cases", icon: FolderOpen, badge: s?.critical_cases },
        { id: "learners", label: "Learners", icon: Users },
        { id: "plans", label: "Support plans", icon: ClipboardList },
        { id: "assessments", label: "Assessments", icon: ClipboardCheck },
        { id: "referrals", label: "Referrals", icon: Send, badge: s?.pending_referrals },
        { id: "resources", label: "Resources", icon: BookOpen },
      ]
    : [{ id: "resources", label: "Resources", icon: BookOpen }];
  const fallback: Tab = canCounsel ? "sessions" : "resources";
  const tab: Tab = tabs.find((item) => item.id === params.get("tab"))?.id ?? fallback;
  const go = (next: Tab, extra: Record<string, string> = {}) => {
    const query: Record<string, string> = { ...extra };
    if (next !== fallback) query.tab = next;
    setParams(query, { replace: true });
  };
  const dash = (value?: number) => (stats.isLoading || value === undefined ? "–" : String(value));
  const tiles: { label: string; value: string; note: string; go: () => void; icon: LucideIcon; alert?: boolean }[] = [
    { label: "Sessions today", value: dash(s?.sessions_today), note: s?.overdue_sessions ? `${s.overdue_sessions} overdue from earlier` : `${s?.scheduled_sessions ?? 0} booked in all`, go: () => go("sessions", { view: s?.overdue_sessions ? "overdue" : "today" }), icon: CalendarDays, alert: Boolean(s?.overdue_sessions) },
    { label: "Follow-ups due", value: dash(s?.follow_ups_due), note: "Nothing booked since", go: () => go("sessions", { view: "follow_up" }), icon: CalendarClock, alert: Boolean(s?.follow_ups_due) },
    { label: "Open cases", value: dash(s?.active_issues), note: s?.critical_cases ? `${s.critical_cases} high or critical` : "None high or critical", go: () => go("cases", { view: s?.critical_cases ? "urgent" : "open" }), icon: AlertTriangle, alert: Boolean(s?.critical_cases) },
    { label: "Active support plans", value: dash(s?.active_plans), note: s?.unassigned_cases ? `${s.unassigned_cases} cases not assigned` : `${s?.scheduled_assessments ?? 0} assessments booked`, go: () => go(s?.unassigned_cases ? "cases" : "plans", s?.unassigned_cases ? { view: "unassigned" } : {}), icon: ClipboardList, alert: Boolean(s?.unassigned_cases) },
  ];
  const panel = { setup, onChanged: refresh, initialView: view };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Counselling</h1>
        <p className="mt-1 text-sm text-gray-500">{canCounsel ? "Sessions, cases, support plans, assessments and referrals for the learners you support." : "Help and reading from the school's counsellors."}</p>
      </div>
      {canCounsel && (
        <p className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
          <span>Confidential. {isAdmin ? "You see every counsellor's records." : "Only you and the administrator see these records."} Notes, findings and case details are never sent to the writing assistant; it only helps word short administrative reasons.</span>
        </p>
      )}

      {canCounsel && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {tiles.map((tile) => (
            <button key={tile.label} type="button" onClick={tile.go} className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}><tile.icon className="h-5 w-5" /></span>
              <span className="min-w-0">
                <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
                <span className={`block truncate text-xl font-semibold tabular-nums ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
                <span className="block truncate text-xs text-gray-500">{tile.note}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {tabs.length > 1 && (
        <nav role="tablist" aria-label="Counselling sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
          {tabs.map((item) => (
            <button key={item.id} type="button" role="tab" aria-selected={tab === item.id} onClick={() => go(item.id)} className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${tab === item.id ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"}`}>
              <item.icon className="h-4 w-4" /> {item.label}
              {item.badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{item.badge}</span> : null}
            </button>
          ))}
        </nav>
      )}

      {tab === "sessions" && <SessionsPanel rows={sessions.data} isLoading={sessions.isLoading} isError={sessions.isError} onRetry={() => sessions.refetch()} initialSearch={learner} {...panel} />}
      {tab === "cases" && <CasesPanel rows={issues.data} isLoading={issues.isLoading} isError={issues.isError} onRetry={() => issues.refetch()} {...panel} />}
      {tab === "learners" && (
        <LearnersPanel
          records={{ sessions: sessions.data ?? [], issues: issues.data ?? [], plans: plans.data ?? [], referrals: referrals.data ?? [], assessments: assessments.data ?? [] }}
          isLoading={sessions.isLoading || issues.isLoading || plans.isLoading}
          onOpenSessions={(file) => go("sessions", { learner: file.student_number || file.student_name })}
        />
      )}
      {tab === "plans" && <PlansPanel rows={plans.data} isLoading={plans.isLoading} isError={plans.isError} onRetry={() => plans.refetch()} {...panel} />}
      {tab === "assessments" && <AssessmentsPanel rows={assessments.data} isLoading={assessments.isLoading} isError={assessments.isError} onRetry={() => assessments.refetch()} {...panel} />}
      {tab === "referrals" && <ReferralsPanel rows={referrals.data} isLoading={referrals.isLoading} isError={referrals.isError} onRetry={() => referrals.refetch()} {...panel} />}
      {tab === "resources" && <ResourcesPanel rows={resources.data} canManage={canCounsel} isLoading={resources.isLoading} isError={resources.isError} onRetry={() => resources.refetch()} onChanged={refresh} />}
    </div>
  );
}
